/**
 * FormularioGuia — Los datos que la Res. SII 154 exige para despachar.
 *
 * Desde el 1-nov-2026 una guía sin chofer, sin cédula, sin comuna de destino o sin la
 * hora de inicio del traslado no cumple, y emitirla cae en el art. 109 del Código
 * Tributario. Antes esto se emitía con dos datos; ahora hay un formulario, y no es
 * burocracia agregada por nosotros.
 *
 * Dos detalles que no son estéticos:
 *   · La fecha y hora son las del INICIO DEL TRASLADO, no las de hoy. El campo arranca en
 *     "ahora" por comodidad, pero se puede mover: una guía se prepara la noche antes y el
 *     camión sale a las siete.
 *   · La patente es el único dato que la ley deja declarar como desconocido. El chofer no.
 *     Por eso hay una casilla para la patente y no la hay para nada más.
 *
 * El servidor vuelve a revisar todo esto: acá se valida para no hacer ir y volver a
 * alguien parado en el portón, no porque el navegador sea la defensa.
 */
import React from "react";

const C = { linea: "#2e2e2e", fondo: "#161616", texto: "#ddd", tenue: "#8a8a8a", alerta: "#f39c12" };

const campo = { background: "#111", border: `1px solid ${C.linea}`, borderRadius: 5,
                padding: "7px 10px", color: C.texto, fontSize: 13, width: "100%", boxSizing: "border-box" };

const Fila = ({ label, hint, children }) => (
  <div style={{ marginBottom: 9 }}>
    <div style={{ fontSize: 10, color: C.tenue, textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 3 }}>
      {label}
    </div>
    {children}
    {hint && <div style={{ fontSize: 10, color: C.tenue, marginTop: 2 }}>{hint}</div>}
  </div>
);

const FormularioGuia = ({ valor, onCambio, tiposTraslado = [] }) => {
  const v = valor || {};
  const set = (camino, val) => {
    const partes = camino.split(".");
    const copia = JSON.parse(JSON.stringify(v));
    let o = copia;
    for (let i = 0; i < partes.length - 1; i++) { o[partes[i]] = o[partes[i]] || {}; o = o[partes[i]]; }
    o[partes[partes.length - 1]] = val;
    onCambio(copia);
  };
  const t = v.transporte || {};
  const d = v.destino || {};
  const sinVenta = ["2.2", "2.3", "2.4", "2.5", "2.6", "2.7"].includes(v.tipoTraslado);

  return (
    <div style={{ background: C.fondo, border: `1px solid ${C.linea}`, borderRadius: 6, padding: "12px 14px", marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: C.alerta, marginBottom: 4 }}>
        🚚 Datos de la guía — Res. SII 154, exigibles desde el 1-nov-2026
      </div>
      {/* Hasta el 31-oct se puede emitir sin estos campos y sin sanción (Res. SII 52).
          Decirlo evita dos cosas: que alguien crea que no puede despachar hoy, y que
          alguien invente un chofer para que el formulario lo deje pasar. */}
      <div style={{ fontSize: 10, color: C.tenue, marginBottom: 10 }}>
        Hasta el 31 de octubre se puede emitir sin ellos. Si falta alguno se avisa y queda
        anotado — no inventes datos para que pase.
      </div>

      <Fila label="Tipo de traslado">
        <select style={campo} value={v.tipoTraslado || ""} onChange={e => set("tipoTraslado", e.target.value)}>
          <option value="">Elegir…</option>
          {tiposTraslado.map(tt => <option key={tt.codigo} value={tt.codigo}>{tt.codigo} · {tt.etiqueta}</option>)}
        </select>
      </Fila>

      <Fila label="Inicio del traslado" hint="Cuándo sale el camión — no es la fecha de emisión del documento.">
        <input type="datetime-local" style={campo} value={v.inicioTraslado || ""}
          onChange={e => set("inicioTraslado", e.target.value)} />
      </Fila>

      <Fila label="Dirección de destino">
        <input style={campo} value={d.direccion || ""} placeholder="Dónde se entrega de verdad"
          onChange={e => set("destino.direccion", e.target.value)} />
      </Fila>
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 1 }}>
          <Fila label="Comuna de destino">
            <input style={campo} value={d.comuna || ""} onChange={e => set("destino.comuna", e.target.value)} />
          </Fila>
        </div>
        <div style={{ flex: 1 }}>
          <Fila label="Ciudad">
            <input style={campo} value={d.ciudad || ""} onChange={e => set("destino.ciudad", e.target.value)} />
          </Fila>
        </div>
      </div>

      <div style={{ borderTop: `1px solid ${C.linea}`, margin: "6px 0 10px" }} />

      <Fila label="RUT del transportista" hint="Rocmin transporta con choferes propios; se puede cambiar si algún día subcontratan.">
        <input style={campo} value={t.rutTransportista || ""} onChange={e => set("transporte.rutTransportista", e.target.value)} />
      </Fila>
      <Fila label="Nombre completo del chofer">
        <input style={campo} value={t.choferNombre || ""} onChange={e => set("transporte.choferNombre", e.target.value)} />
      </Fila>
      <Fila label="Cédula del chofer">
        <input style={campo} value={t.choferCedula || ""} onChange={e => set("transporte.choferCedula", e.target.value)} />
      </Fila>

      <Fila label="Patente">
        <input style={campo} value={t.patente || ""} disabled={!!t.patenteNoConocida}
          onChange={e => set("transporte.patente", e.target.value)} />
      </Fila>
      <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 11, color: C.tenue, marginBottom: 9 }}>
        <input type="checkbox" checked={!!t.patenteNoConocida}
          onChange={e => { set("transporte.patenteNoConocida", e.target.checked); }} />
        No se conoce la patente — se declara expresamente en el documento
      </label>

      {sinVenta && (
        <>
          <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 11, color: C.tenue, marginBottom: 6 }}>
            <input type="checkbox" checked={!!v.sinPrecio?.es} onChange={e => set("sinPrecio.es", e.target.checked)} />
            Traslado que no constituye venta: omitir el precio unitario
          </label>
          {v.sinPrecio?.es && (
            <Fila label="Motivo de la omisión" hint="La ley permite omitir el precio, pero exige decir por qué.">
              <input style={campo} value={v.sinPrecio?.motivo || ""} onChange={e => set("sinPrecio.motivo", e.target.value)} />
            </Fila>
          )}
        </>
      )}

      <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 11, color: C.tenue, marginBottom: 6 }}>
        <input type="checkbox" checked={!!v.trasladoProlongado?.es}
          onChange={e => set("trasladoProlongado.es", e.target.checked)} />
        El traslado dura más de un día
      </label>
      {v.trasladoProlongado?.es && (
        <>
          <Fila label="Motivo del traslado prolongado">
            <input style={campo} value={v.trasladoProlongado?.motivo || ""}
              onChange={e => set("trasladoProlongado.motivo", e.target.value)} />
          </Fila>
          <Fila label="Fecha estimada de entrega" hint="Sin esto, la guía sólo vale el día de emisión.">
            <input type="date" style={campo} value={v.trasladoProlongado?.fechaEstimadaEntrega || ""}
              onChange={e => set("trasladoProlongado.fechaEstimadaEntrega", e.target.value)} />
          </Fila>
        </>
      )}
    </div>
  );
};

/** Lo mismo que revisa el servidor, para avisar antes de ir y volver. */
export const faltantesGuia = v => {
  const f = [];
  const t = v?.transporte || {};
  const d = v?.destino || {};
  if (!v?.tipoTraslado) f.push("tipo de traslado");
  if (!v?.inicioTraslado) f.push("inicio del traslado");
  if (!d.direccion) f.push("dirección de destino");
  if (!d.comuna) f.push("comuna de destino");
  if (!t.rutTransportista) f.push("RUT del transportista");
  if (!t.choferNombre) f.push("nombre del chofer");
  if (!t.choferCedula) f.push("cédula del chofer");
  if (!t.patente && !t.patenteNoConocida) f.push("patente (o declarar que no se conoce)");
  if (v?.sinPrecio?.es && !v?.sinPrecio?.motivo) f.push("motivo de la omisión del precio");
  if (v?.trasladoProlongado?.es && !v?.trasladoProlongado?.motivo) f.push("motivo del traslado prolongado");
  if (v?.trasladoProlongado?.es && !v?.trasladoProlongado?.fechaEstimadaEntrega) f.push("fecha estimada de entrega");
  return f;
};

export default FormularioGuia;
